import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import type { RevisionPair, ExtractionProposal, SimilarRule, QueuedRule } from '../types';
import Spinner from '../components/Spinner';
import './LearningPage.css';

// 2026-10-22 (Odin's ruling): approving a proposal never writes to a live
// prompt. Approve (or Save & approve from Merge) puts the rule in the
// approved, waiting queue with its target prompt; a Code session on the Mac
// folds queued rules into a new canonical prompt version and publishes it.
// Revisions come from the Content page: every Edit and every Regenerate with
// a direction.

/** Extended proposal with fields populated by the API / enrichment step */
interface EnrichedProposal extends ExtractionProposal {
  proposedRule: string;
  pattern: string;
  supportingPairs?: number;
  revisionIds?: string[];
  similar: SimilarRule[];
}

interface MergeState {
  proposal: EnrichedProposal;
  similarRule: SimilarRule;
}

interface ProposalCardProps {
  proposal: EnrichedProposal;
  busy: boolean;
  allowed: string[];
  onApprove: (targetFile: string) => void;
  onReject: () => void;
  onMerge: (similarRule: SimilarRule) => void;
}

interface MergeEditorProps {
  mergeState: MergeState;
  onSave: (rule: string) => Promise<void>;
  onClose: () => void;
}

export default function LearningPage() {
  const [revisions, setRevisions] = useState<RevisionPair[]>([]);
  const [grouped, setGrouped] = useState<Record<string, RevisionPair[]>>({});
  const [tags, setTags] = useState<string[]>([]);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [extracting, setExtracting] = useState<boolean>(false);
  const [applyingProposal, setApplyingProposal] = useState<EnrichedProposal | null>(null);
  const [proposals, setProposals] = useState<EnrichedProposal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mergeState, setMergeState] = useState<MergeState | null>(null);
  const [queue, setQueue] = useState<QueuedRule[]>([]);
  const [allowed, setAllowed] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  // Fake judge agreement (2026-10-22): the judge's top three against Odin's
  // approved Stage 1b picks over the last 10 words.
  const [judgeLine, setJudgeLine] = useState<string | null>(null);

  const fetchJudge = useCallback(async (): Promise<void> => {
    try {
      const d = await api.get('/api/learning/judge-agreement') as { words: number; agreed: number; picks: number };
      setJudgeLine(d.words
        ? `Judge agreed with you on ${d.agreed} of ${d.picks} picks over the last ${d.words} word${d.words === 1 ? '' : 's'}.`
        : 'Judge agreement: no Stage 1b approved with a judge verdict yet.');
    } catch (err) {
      setJudgeLine(`Judge agreement could not be read: ${(err as Error).message}`);
    }
  }, []);

  const fetchQueue = useCallback(async (): Promise<void> => {
    try {
      const data = await api.get('/api/learning/queue') as { queue?: QueuedRule[]; allowed_targets?: string[] };
      setQueue(data.queue || []);
      setAllowed(data.allowed_targets || []);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  // Proposals already made and still pending, so they survive a reload.
  const fetchPending = useCallback(async (): Promise<void> => {
    try {
      const data = await api.get('/api/learning/proposals?status=pending') as { proposals?: EnrichedProposal[] };
      setProposals((prev) => (prev.length ? prev : (data.proposals || []).map((p) => ({ ...p, similar: [] }))));
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  const fetchRevisions = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await api.get('/api/learning/revisions?status=open') as {
        revisions?: RevisionPair[];
        grouped?: Record<string, RevisionPair[]>;
        tags?: string[];
      };
      setRevisions(data.revisions || []);
      setGrouped(data.grouped || {});
      setTags(data.tags || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRevisions();
    fetchQueue();
    fetchPending();
    fetchJudge();
  }, [fetchRevisions, fetchQueue, fetchPending, fetchJudge]);

  const handleExtract = useCallback(async (tag: string | null): Promise<void> => {
    setExtracting(true);
    setError(null);
    try {
      const data = await api.post('/api/learning/extract', { tag: tag || undefined }) as {
        proposals?: EnrichedProposal[];
      };
      const rawProposals: EnrichedProposal[] = data.proposals || [];

      if (rawProposals.length > 0) {
        try {
          const simData = await api.post('/api/learning/find-similar', { proposals: rawProposals }) as {
            results?: Array<{ similar?: SimilarRule[] }>;
          };
          const results = simData.results || [];
          const enriched: EnrichedProposal[] = rawProposals.map((p, i) => ({
            ...p,
            similar: results[i]?.similar || [],
          }));
          setProposals(enriched);
        } catch (simErr) {
          setProposals(rawProposals.map((p) => ({ ...p, similar: [] })));
          setError((simErr as Error).message);
        }
      } else {
        setProposals([]);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setExtracting(false);
    }
  }, []);

  const handleResolve = useCallback(async (id: string): Promise<void> => {
    try {
      await api.put(`/api/learning/revisions/${id}`, { status: 'resolved' });
      setRevisions((prev) => prev.filter((r) => r.id !== id));
      setGrouped((prev) => {
        const next = { ...prev };
        for (const tag of Object.keys(next)) {
          next[tag] = next[tag].filter((r) => r.id !== id);
          if (next[tag].length === 0) delete next[tag];
        }
        return next;
      });
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  const approve = useCallback(async (
    proposal: EnrichedProposal,
    body: { target_file?: string; rule?: string; merge_with?: SimilarRule },
  ): Promise<void> => {
    if (!proposal.id) throw new Error('This proposal has no stored id. Run extraction again.');
    const r = await api.post(`/api/learning/proposals/${proposal.id}/approve`, body) as { queued?: QueuedRule };
    setProposals((prev) => prev.filter((p) => p !== proposal));
    setNotice(`Queued for ${r.queued?.target_file || body.target_file || proposal.targetFile}. It reaches the live prompt only when a Code session folds it into a new prompt version and publishes it.`);
    fetchRevisions();
    fetchQueue();
  }, [fetchRevisions, fetchQueue]);

  const handleApprove = useCallback(async (proposal: EnrichedProposal, targetFile: string): Promise<void> => {
    if (applyingProposal) return;
    setApplyingProposal(proposal);
    setError(null);
    try {
      await approve(proposal, { target_file: targetFile });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setApplyingProposal(null);
    }
  }, [approve, applyingProposal]);

  const handleReject = useCallback((proposal: EnrichedProposal): void => {
    setProposals((prev) => prev.filter((p) => p !== proposal));
    if (proposal.id) api.put(`/api/learning/proposals/${proposal.id}`, { status: 'rejected' }).catch(() => {});
    if (proposal.revisionIds) {
      for (const id of proposal.revisionIds) {
        api.put(`/api/learning/revisions/${id}`, { status: 'rejected' }).catch(() => {});
      }
    }
  }, []);

  const handleStartMerge = useCallback((proposal: EnrichedProposal, similarRule: SimilarRule): void => {
    setMergeState({ proposal, similarRule });
  }, []);

  // Merge (2026-10-22): Odin writes the merged wording; it is queued with the
  // rule it merges into. The prompt file itself is not edited here.
  const handleMergeSave = useCallback(async (rule: string): Promise<void> => {
    if (!mergeState) return;
    try {
      const target = allowed.includes(mergeState.similarRule.file) ? mergeState.similarRule.file : mergeState.proposal.targetFile;
      await approve(mergeState.proposal, { target_file: target, rule, merge_with: mergeState.similarRule });
      setMergeState(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [mergeState, approve, allowed]);

  const withdraw = useCallback(async (item: QueuedRule): Promise<void> => {
    try {
      await api.put(`/api/learning/queue/${item.id}`, { status: 'withdrawn' });
      fetchQueue();
    } catch (err) {
      setError((err as Error).message);
    }
  }, [fetchQueue]);

  const waiting = queue.filter((q) => q.status === 'waiting');

  const displayRevisions = activeTag ? (grouped[activeTag] || []) : revisions;

  return (
    <div className="learning-page">
      {mergeState && (
        <MergeEditor
          mergeState={mergeState}
          onSave={handleMergeSave}
          onClose={() => setMergeState(null)}
        />
      )}

      <div className="learning-header">
        <h1 className="learning-title">Learning Layer</h1>
        <p className="learning-subtitle">
          {revisions.length} open revision{revisions.length !== 1 ? 's' : ''} captured · {waiting.length} approved rule{waiting.length !== 1 ? 's' : ''} waiting
        </p>
        {judgeLine && <p className="learning-subtitle">{judgeLine}</p>}
      </div>

      {notice && (
        <div className="learning-notice">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)}>Dismiss</button>
        </div>
      )}

      {error && (
        <div className="learning-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <div className="learning-layout">
        <div className="learning-revisions">
          <div className="learning-tags">
            <button
              className={`learning-tag ${!activeTag ? 'tag-active' : ''}`}
              onClick={() => setActiveTag(null)}
            >
              All ({revisions.length})
            </button>
            {tags.map((tag) => (
              <button
                key={tag}
                className={`learning-tag ${activeTag === tag ? 'tag-active' : ''}`}
                onClick={() => setActiveTag(tag)}
              >
                {formatTag(tag)} ({(grouped[tag] || []).length})
              </button>
            ))}
          </div>

          <button
            className="learning-extract-btn"
            onClick={() => handleExtract(activeTag)}
            disabled={extracting || displayRevisions.length === 0}
          >
            {extracting ? 'Analyzing...' : `Extract Patterns${activeTag ? ` (${formatTag(activeTag)})` : ''}`}
          </button>

          {loading && <p className="learning-loading">Loading revisions...</p>}

          {!loading && displayRevisions.length === 0 && (
            <p className="learning-empty">No open revisions. Every Edit, every Regenerate with a direction and every saved Your direction on the Content page adds one.</p>
          )}

          <div className="revision-list">
            {displayRevisions.map((rev) => (
              <div key={rev.id} className="revision-card">
                <div className="revision-header">
                  <span className="revision-word">{rev.word}</span>
                  <span className="revision-field">{rev.stage ? `Stage ${rev.stage} · ` : ''}{rev.field}</span>
                  {rev.how && <span className="revision-tag">{rev.how === 'regenerate' ? 'Regenerate' : rev.how === 'judge' ? 'Approve 1b' : rev.how === 'direction' ? 'Your direction' : 'Edit'}</span>}
                  <span className="revision-tag">{formatTag(rev.tag)}</span>
                  {rev.edit_ratio != null && (
                    <span className="revision-ratio">{Math.round(rev.edit_ratio * 100)}% changed</span>
                  )}
                  <span className="revision-time">{formatTime(rev.timestamp)}</span>
                </div>
                {rev.how === 'direction' ? (
                <div className="revision-diff">
                  {rev.original && (
                    <div className="revision-original">
                      <span className="diff-label">Replaced</span>
                      <span className="diff-text">{rev.original}</span>
                    </div>
                  )}
                  <div className="revision-direction">
                    <span className="diff-label">Direction</span>
                    <span className="diff-text">{rev.direction}</span>
                  </div>
                </div>
                ) : (
                <div className={`revision-diff${rev.judge ? ' revision-judge' : ''}`}>
                  <div className="revision-original">
                    <span className="diff-label">{rev.judge ? 'Judge' : 'Original'}</span>
                    <span className="diff-text">{rev.original}</span>
                  </div>
                  <div className="revision-revised">
                    <span className="diff-label">{rev.judge ? 'Odin' : 'Revised'}</span>
                    <span className="diff-text">{rev.revised}</span>
                  </div>
                  {rev.direction && (
                    <div className="revision-direction">
                      <span className="diff-label">Direction</span>
                      <span className="diff-text">{rev.direction}</span>
                    </div>
                  )}
                </div>
                )}
                <div className="revision-actions">
                  <button className="revision-resolve" onClick={() => handleResolve(rev.id)}>
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="learning-proposals">
          <h2 className="proposals-title">Proposals</h2>

          {proposals.length === 0 && !extracting && (
            <p className="proposals-empty">
              Run extraction to analyze revision pairs and generate prompt improvement proposals.
            </p>
          )}

          {extracting && (
            <div className="proposals-loading">
              <div className="generating-spinner" />
              <p>Analyzing patterns...</p>
            </div>
          )}

          {proposals.map((proposal, idx) => (
            <ProposalCard
              key={proposal.id || idx}
              proposal={proposal}
              busy={applyingProposal === proposal}
              allowed={allowed}
              onApprove={(targetFile) => handleApprove(proposal, targetFile)}
              onReject={() => handleReject(proposal)}
              onMerge={(similarRule) => handleStartMerge(proposal, similarRule)}
            />
          ))}

          <h2 className="proposals-title queue-title">Approved, waiting</h2>
          <p className="proposals-empty">
            Approved rules wait here with their target prompt. None is live until a Code session folds it into a new prompt version and publishes it.
          </p>
          {queue.length === 0 && <p className="proposals-empty">No approved rules yet.</p>}
          {queue.map((item) => (
            <div key={item.id} className={`proposal-card queue-card queue-${item.status}`}>
              <div className="proposal-header">
                <span className="proposal-confidence">{item.status}</span>
                <span className="proposal-target">{item.target_file}</span>
              </div>
              <div className="proposal-rule-text">{item.rule}</div>
              {item.merge_with && (
                <div className="similar-rule">
                  <span className="similar-label">Merge with {item.merge_with.file}{item.merge_with.line ? `:${item.merge_with.line}` : ''}:</span>
                  <div className="similar-rule-text">{item.merge_with.text}</div>
                </div>
              )}
              <span className="proposal-pairs">
                Approved {formatTime(item.approved_at)}
                {item.status === 'folded' && item.prompt_version ? ` · folded into ${item.prompt_version}` : ''}
              </span>
              {item.status === 'waiting' && (
                <div className="proposal-actions">
                  <button className="proposal-reject" onClick={() => withdraw(item)}>Withdraw</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProposalCard({ proposal, busy, allowed, onApprove, onReject, onMerge }: ProposalCardProps) {
  // A target the analyst named that no longer exists (or never did) is swapped
  // for an allowed prompt file before Approve.
  const targetOk = allowed.includes(proposal.targetFile);
  const [target, setTarget] = useState<string>(targetOk ? proposal.targetFile : '');
  return (
    <div className="proposal-card">
      <div className="proposal-header">
        <span className={`proposal-confidence conf-${proposal.confidence}`}>
          {proposal.confidence}
        </span>
        <span className="proposal-target">{proposal.targetFile}</span>
      </div>
      <div className="proposal-pattern">{proposal.pattern}</div>
      <div className="proposal-rule">
        <span className="proposal-rule-label">Proposed Rule</span>
        <div className="proposal-rule-text">{proposal.proposedRule}</div>
      </div>

      {proposal.similar && proposal.similar.length > 0 && (
        <div className="proposal-similar">
          <span className="similar-label">Similar existing rules:</span>
          {proposal.similar.map((sim, i) => (
            <div key={i} className="similar-rule">
              <div className="similar-rule-header">
                <span className="similar-file">{sim.file}:{sim.line}</span>
                <button className="similar-merge-btn" onClick={() => onMerge(sim)}>
                  Merge
                </button>
              </div>
              <div className="similar-rule-text">{sim.text}</div>
            </div>
          ))}
        </div>
      )}

      {proposal.supportingPairs && (
        <span className="proposal-pairs">
          Based on {proposal.supportingPairs} revision pair{proposal.supportingPairs !== 1 ? 's' : ''}
        </span>
      )}
      {!targetOk && allowed.length > 0 && (
        <label className="proposal-target-pick">
          Target prompt:{' '}
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Pick a prompt file</option>
            {allowed.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </label>
      )}
      <div className="proposal-actions">
        <button className="proposal-apply" onClick={() => onApprove(target)} disabled={busy || !target || !proposal.id}
          title="Queue this rule for its target prompt. Nothing changes in the live prompt.">
          {busy ? <><Spinner />Queuing…</> : 'Approve (queue)'}
        </button>
        <button className="proposal-reject" onClick={onReject} disabled={busy}>
          Reject
        </button>
      </div>
    </div>
  );
}

function MergeEditor({ mergeState, onSave, onClose }: MergeEditorProps) {
  const [rule, setRule] = useState<string>(mergeState.proposal.proposedRule);
  const [saving, setSaving] = useState<boolean>(false);

  return (
    <div className="merge-overlay">
      <div className="merge-modal">
        <div className="merge-header">
          <h3 className="merge-title">Merge into {mergeState.similarRule.file}</h3>
          <button className="merge-close" onClick={onClose}>&times;</button>
        </div>
        <div className="merge-body">
          <div className="merge-editor-pane">
            <span className="merge-pane-label">Merged rule (queued, not written to the prompt)</span>
            <textarea className="merge-rule-input" value={rule} rows={10} onChange={(e) => setRule(e.target.value)} />
          </div>
          <div className="merge-reference-pane">
            <span className="merge-pane-label">Proposed Rule (reference)</span>
            <div className="merge-reference-text content-text">
              {mergeState.proposal.proposedRule}
            </div>
            <div className="merge-similar-context">
              <span className="merge-pane-label">Similar existing rule (line ~{mergeState.similarRule.line || 1})</span>
              <div className="merge-similar-text content-text">
                {mergeState.similarRule.text}
              </div>
            </div>
          </div>
        </div>
        <div className="merge-footer">
          <button
            className="merge-save-btn"
            disabled={saving || !rule.trim()}
            onClick={async () => { setSaving(true); try { await onSave(rule); } finally { setSaving(false); } }}
          >
            {saving ? <><Spinner />Queuing…</> : 'Save & approve (queue)'}
          </button>
          <button className="merge-cancel-btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function formatTag(tag: string): string {
  return tag.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}
